import { useContext, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useParams } from 'react-router-dom';
import {
  PatientOrderInfo,
  ROLE,
  TrackingEvent,
  TrackingHistoryData,
} from '../interface/interface';
import { getOrderInfoForPatient } from '../service';
import Loading from './Loading';
import PatientPrimaryInfo from './PatientPrimaryInfo';
import CollapsibleDataTable from './TableWithCollapsibleRows';
import { fetchTrackingInfo } from '../service/fetchTrackingInfo';
import { fetchTrackingInfoPatient } from '../service/fetchTrackingInfoPatient';
import { AuspostTrackingURL } from '../constant/constant';
import Breadcrumbs from './Breadcrumbs';
import { UserContext } from '../context/userContext';
import { nativeService } from '../helpers/nativeService';
import moment from 'moment';

export function TrackingHistory() {
  const { orderId } = useParams();
  const { user } = useAuth();
  const { setActiveItem, isMobile, nativeView } = useContext(UserContext);

  const [invoiceId, setInvoiceId] = useState<number>();
  const [patientDetail, setPatientDetail] = useState<PatientOrderInfo>();
  const visibleColumns = ['Location', 'Date', 'Description'];
  const [trackingEvents, setTrackingEvents] = useState<Array<TrackingEvent>>(
    [],
  );

  const [patientDataLoading, setPatientDataLoading] = useState(false);
  const [trackingHistoryLoading, setTrackingHistoryLoading] = useState(false);

  useEffect(() => {
    setActiveItem('My Care Hub');
  }, [isMobile]);

  useEffect(() => {
    if (!orderId || !user?.access_token || isNaN(Number(orderId))) return;
    setPatientDataLoading(true);
    getOrderInfoForPatient(user.access_token, Number(orderId))
      .then((data) => {
        setPatientDetail(data);
        setInvoiceId(data.invoiceId);
        setPatientDataLoading(false);
      })
      .catch((err) => {
        setPatientDataLoading(false);
        console.log(err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  useEffect(() => {
    if (!orderId || !user?.access_token || isNaN(Number(orderId))) return;
    setTrackingHistoryLoading(true);

    const fnToGetTrackingInfo = (user.profile.groups as Array<string>).includes(
      ROLE.Patients,
    )
      ? fetchTrackingInfoPatient
      : fetchTrackingInfo;

    fnToGetTrackingInfo(user.access_token, Number(orderId))
      .then((data) => {
        setTrackingEvents(data);
        setTrackingHistoryLoading(false);
      })
      .catch((err) => {
        setTrackingHistoryLoading(false);
        console.log(err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  if (patientDataLoading || trackingHistoryLoading) return <Loading />;

  const adminRoutes = [
    {
      tag: 'a',
      className: 'mr-3',
      href: '/admin/orders',
      children: <>Orders</>,
    },
    {
      tag: 'a',
      className: 'mr-3',
      href: `/order-summary/${orderId}`,
      children: <>Order summary #{invoiceId}</>,
    },
    {
      tag: 'p',
      children: <span>Tracking History</span>,
    },
  ];

  const patientRoutes = [
    {
      tag: 'a',
      className: 'mr-3',
      href: '/orders',
      children: <>Orders</>,
    },
    {
      tag: 'a',
      className: 'mr-3',
      href: `/order-summary-patient/${orderId}`,
      children: <>Order summary #{invoiceId}</>,
    },
    {
      tag: 'p',
      children: <span>Tracking History</span>,
    },
  ];

  return (
    <>
      <Breadcrumbs
        data={
          (user?.profile.groups as Array<string>).includes(ROLE.Patients)
            ? patientRoutes
            : adminRoutes
        }
      />
      <h2 className="text-xl md:text-2xl text-primary-blue-500 pb-5 font-semibold">
        Tracking History for{' '}
        <span className="text-secondary-pink-600">#{invoiceId ?? '-'}</span>
      </h2>
      {patientDetail && (
        <PatientPrimaryInfo
          patient={{
            ...patientDetail,
            id: patientDetail.patientId,
            patientNotes: '',
          }}
        />
      )}
      <div className="bg-white p-6 md:p-10 rounded-md shadow mt-10">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center !mb-11">
          <h2 className="text-2xl text-primary-blue-500 font-semibold">
            Order tracking{' '}
            {invoiceId && (
              <span className="text-secondary-pink-600 ml-6">#{invoiceId}</span>
            )}
          </h2>
          {patientDetail?.auspostId && (
            <button
              className="bg-secondary-pink-600 text-white px-6 py-3 text-sm md:text-base font-semibold rounded h-50 mt-5 md:mt-0"
              onClick={() => {
                if (nativeView) {
                  nativeService(
                    JSON.stringify({
                      trackingUrl: `${AuspostTrackingURL}/${patientDetail?.auspostId}`,
                    }),
                  );
                } else {
                  window.open(
                    `${AuspostTrackingURL}/${patientDetail?.auspostId}`,
                    '_blank',
                  );
                }
              }}
            >
              Track in Auspost
            </button>
          )}
        </div>
        {trackingEvents.length ? (
          <>
            <div className="hidden xl:block">
              <CollapsibleDataTable
                data={trackingEvents.map(
                  (e) =>
                    ({
                      Location: e.location,
                      Date: moment(e.date).isValid()
                        ? moment(e.date).format('ddd, D MMM YYYY hh:mm A')
                        : '-',
                      Description: e.description,
                    }) as TrackingHistoryData,
                )}
                visibleColumns={visibleColumns}
                hiddenColumns={[]}
                className="order-history-table"
              />
            </div>
            <div className="block xl:hidden">
              <div className="p-4 bg-secondary-pink-50 rounded-md">
                <div className="relative border-l-2 border-primary-blue-200 ml-4">
                  {trackingEvents?.map((d, index) => (
                    <div
                      key={index}
                      className={`ml-6 relative ${index !== trackingEvents.length - 1 ? 'mb-8' : ''}`}
                    >
                      <span className="absolute left-[-33px] w-4 h-4 bg-primary-blue-500 rounded-full border-2 border-white"></span>

                      <p className="text-sm text-primary-blue-500 font-regular mb-2">
                        {moment(d.date).isValid()
                          ? moment(d.date).format('ddd, D MMM YYYY hh:mm A')
                          : '-'}
                      </p>

                      <p className="text-sm text-primary-blue-500 font-regular mb-2">
                        {d.location}
                      </p>

                      <p className="text-sm text-primary-blue-500 font-regular">
                        {d.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        ) : (
          <p className="text-center font-semibold">No events available</p>
        )}
      </div>
    </>
  );
}
